import type { GroupRoleMapping } from '@/utils/roles'

export interface QuickLink {
  name: string
  description?: string
  icon: string
  url: string
  category: string
  categoryOrderNumber: number
  orderNumber: number
  environment?: 'PROD' | 'STAGING' | 'TEST' | 'DEMO'
}

// TenantContext mirrors the registry response DTO. It deliberately carries no
// groupRoleMapping: the group-to-role assignment is internal to the backend.
export interface TenantContext {
  customerNo: string
  customerName?: string
  tenantName: string
  primaryColor?: string
  isDefault?: boolean
  groupNameMapping?: Record<string, string>
  // Set for tenants a portal admin can reach only via the admin bypass.
  portalAdminOnly?: boolean
}

// A switch of a user from another customer into a tenant of the customer.
export interface AccessLogEntry {
  timestamp: string
  actor: string
  tenantName: string
}

export interface UserInfo {
  user: {
    name: string
    fullName: string
    email: string
    customerNo?: string
    roles?: string[]
    permissions?: string[]
    groups?: string[]
    groupMemberships?: string[]
    groupDetails?: GroupDetail[]
  }
  tenant?: {
    tenantName: string
  }
  activeTenant?: TenantContext
  allowedTenants?: TenantContext[]
  environment: {
    apiUrl: string
    prod: boolean
    activeProfiles: string[]
    logoutUrl?: string
    // Whether customers may use registry-managed local users.
    localLoginEnabled?: boolean
  }
  quickLinks: QuickLink[]
  jwt?: string
  idToken?: string
}

export interface GroupDetail {
  id: string
  displayName: string
}

export interface CustomerId {
  customer: string
  tenant: string
}

export interface UpstreamOIDCConfig {
  issuer: string
  clientId: string
  clientSecret?: string
}

// CustomerRecoveryContact is the projected security channel of a customer. It is
// strictly separate from `brevoEmail`, which is a broad distribution list and
// therefore never receives a link that changes the security configuration.
export interface CustomerRecoveryContact {
  email: string
  verified: boolean
  verifiedAt?: string
  addedAt?: string
}

export interface Customer {
  id?: string | null
  customerNo: string
  customerName: string
  customerShortName: string
  brevoEmail: string
  brevoActive: boolean
  upstreamOIDC?: UpstreamOIDCConfig
  // How users sign in; empty means "oidc". Read-only in the customer list.
  loginMode?: 'oidc' | 'local'
  // Only for local customers: no active administrator exists yet. Absent when unknown.
  localAdminMissing?: boolean
  recoveryContacts?: CustomerRecoveryContact[]
  recoveryCodesRemaining?: number
}

export interface Tenant {
  name: string
  bucketName: string
  isDefault?: boolean
  primaryColor?: string
  allowedEmailRegex?: string[]
  groupRoleMapping?: GroupRoleMapping
  groupNameMapping?: Record<string, string>
}

export interface OIDCClient {
  id: string
  redirect_uris: string[]
  post_logout_redirect_uris?: string[]
  application_type: 0 | 1 | 2
  auth_method: string
  response_types: string[]
  grant_types: string[]
  access_token_type: 0 | 1
  id_token_lifetime: number
  is_dev_mode: boolean
}

export interface OIDCClientSecretResponse extends OIDCClient {
  client_secret?: string
}

export interface TableColumn {
  key: string
  header?: string
  label?: string
  id?: string
  sortable?: boolean
  visible?: boolean
}

export interface GraphFieldArg {
  name: string
  description?: string
  defaultValue?: string | null
  type: {
    name: string | null
    kind: string
    ofType?: {
      name: string | null
      kind: string
      ofType?: {
        name: string | null
        kind: string
      } | null
    } | null
  }
}

export interface GraphField {
  name: string
  description?: string
  args?: GraphFieldArg[]
  type: {
    name: string | null
    kind: string
    ofType?: {
      name: string | null
      kind: string
      ofType?: {
        name: string | null
        kind: string
        ofType?: {
          name: string | null
          kind: string
        } | null
      } | null
    } | null
  }
}

export interface GraphType {
  name: string
  kind: string
  description?: string
  fields: GraphField[] | null
}

export interface IntrospectionResponse {
  data: {
    __type: GraphType | null
  }
}

export interface ColumnConfig {
  key: string
  label: string
  visible: boolean
}

export interface WorkflowField {
  name: string
  label: string
  visible?: boolean
  editable?: boolean
  required?: boolean
}

export interface NextStep {
  condition?: string
  step: string
  label?: string
  color?: string
  icon?: string
}

export interface WorkflowStepConfig {
  name: string
  stepType: string
  group?: string
  user?: string
  actorFromField?: string
  teamsWebhookUrl?: string
  fields?: WorkflowField[]
  nextSteps?: NextStep[]
  action?: string
  allowInquiry?: boolean
  position?: { x: number; y: number }
  style?: Record<string, string>
}

export interface WorkflowOverviewField {
  name: string
  label: string
}

export interface WorkflowOverviewFieldWithValue {
  name: string
  label: string
  value?: string
}

export interface WorkflowBlueprint {
  id: string
  version?: number
  trigger?: {
    type?: string
    topic?: string
    statusId?: string
    objectType?: 'Receipt' | 'Artikel' | 'Adresse' | 'Projekt'
    nextStep?: NextStep
  }
  edgeType?: string
  steps: WorkflowStepConfig[]
  overviewField1?: WorkflowOverviewField
  overviewField2?: WorkflowOverviewField
  inquiryWebhookUrl?: string
  updatedAt?: string
}

export interface HistoryEntry {
  stepName: string
  actor: string
  group?: string
  user?: string
  action: string
  timestamp: string
  comment?: string
  color?: string
  icon?: string
  branch?: string
}

export interface InstanceState {
  guid: string
  objectType: string
  objectNumber: string
  blueprint?: WorkflowBlueprint
  blueprintId: string
  blueprintVersion: string
  customerId: CustomerId
  currentStep: WorkflowStepConfig
  workflowState: string
  claimedBy?: string
  claimedAt?: string
  startTime?: string
  endTime?: string
  retryCount?: number
  lastRetryAt?: string
  data: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
  overviewField1?: WorkflowOverviewFieldWithValue
  overviewField2?: WorkflowOverviewFieldWithValue
  history: HistoryEntry[]
}

export interface TaskDetailsDto {
  instance: InstanceState
  currentStepConfig?: WorkflowStepConfig
  blueprintId: string
  pdfUrl?: string
  data?: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
  document?: {
    fldNa?: string
    fldExt?: string
    fnGetContentsAsBase64?: string
    [key: string]: any // eslint-disable-line @typescript-eslint/no-explicit-any
  }
}

export interface InvoiceStatisticView {
  invoicecorrection: number
  invoice: number
  creditnote: number
  other: number
}

export interface WorkflowUsageView {
  trigger: number
  usertask: number
}

export interface MonthlyStatisticView {
  year: number
  month: number
  invoice: InvoiceStatisticView
  workflow: Record<string, WorkflowUsageView>
  invoiceTotal: number
  workflowTotal: number
  categoryCounts?: Record<string, number>
  einvoiceCount?: number
  sumExcludingOther?: number
  totalSum: number
}

export interface TestCheck {
  label: string
  status: 'success' | 'warning' | 'error' | 'neutral'
  detail: string
}

export interface ValidateWorkflowConfigResponse {
  ok: boolean
  message: string
  checks: TestCheck[]
}

export type UserEmailField = 'fldEMail1' | 'fldEMail2' | 'fldFirEMailInt' | 'fldFirEMail'

export const USER_EMAIL_FIELD_OPTIONS: { label: string; value: UserEmailField }[] = [
  { label: 'E-Mail 1 (Standard)', value: 'fldEMail1' },
  { label: 'E-Mail 2', value: 'fldEMail2' },
  { label: 'E-Mail (von intern)', value: 'fldFirEMailInt' },
  { label: 'E-Mail (von extern)', value: 'fldFirEMail' },
]

export interface FileDto {
  name: string
  mimeType: string
  content: string // Base64
}

export interface EmergencyReportDto {
  id?: string
  timestamp?: string
  customerNo?: string
  tenantName?: string
  environment?: string
  contactPerson: string
  phoneNumber: string
  email: string
  service: string
  priority?: 'low' | 'normal' | 'high' | 'urgent'
  description: string
  businessImpact: string
  startTime: string
  dataLoss: boolean
  securityImpact: boolean
  productionOutage: boolean
  venaboTicket?: string
  gitlabUrl?: string
  files?: FileDto[]
}

export interface ReportDto {
  id?: string
  timestamp?: string
  type: 'emergency' | 'bug' | 'feature'
  title?: string
  customerNo?: string
  tenantName?: string
  environment?: string
  contactPerson: string
  phoneNumber?: string
  email: string
  service: string
  priority?: 'low' | 'normal' | 'high' | 'urgent'
  description?: string
  observedBehavior?: string
  expectedBehavior?: string
  stepsToReproduce?: string
  businessContext?: string
  expectedBenefit?: string
  proposedSolution?: string
  additionalContext?: string
  businessImpact?: string
  startTime?: string
  dataLoss?: boolean
  securityImpact?: boolean
  productionOutage?: boolean
  venaboTicket?: string
  gitlabUrl?: string
  files?: FileDto[]
}

export interface TicketDto {
  iid: number
  title: string
  type: 'bug' | 'feature' | 'emergency'
  status: string
  state: 'opened' | 'closed'
  priority?: string
  service?: string
  visibility: 'private' | 'public'
  private: boolean
  hasOpenQuestion?: boolean
  createdAt: string
  updatedAt: string
}

export interface TicketSectionDto {
  key: string
  label: string
  content: string
}

export interface TicketDetailDto extends TicketDto {
  sections: TicketSectionDto[]
}

export interface TicketEventDto {
  id?: number
  type: 'created' | 'status' | 'priority' | 'state' | 'comment' | 'question'
  status?: string
  comment?: string
  author?: string
  recipient?: string
  visibility?: string
  timestamp: string
}

export interface NfonConfigDto {
  active: boolean
  apiKeyId: string
  apiKeySecret?: string
  customerId: string
  baseUrl?: string
  defaultCountryCode?: string
  syncIntervalCron?: string
  lastSync?: string
  lastSyncStatus?: string
  lastSyncCount?: number
  lastSyncError?: string
  lastUpdate?: string
  lastEditor?: string
}

export interface NfonTestResult {
  success: boolean
  nfonConnected: boolean
  microtechStatus?: string
  message: string
  phoneBookEntries?: number
}

export interface NfonSyncRunLog {
  id: string
  customerNo: string
  tenantName: string
  startTime: string
  endTime: string
  durationMs: number
  status: string
  mode: 'INCREMENTAL' | 'FULL_RESYNC'
  totalFound: number
  created: number
  updated: number
  deleted: number
  unchanged: number
  errors?: string[]
  triggerType: string
}

export interface NfonSyncStatus {
  lastSync?: NfonSyncRunLog
  lastFullSyncTime?: string
  addressesLastLsn?: number
  postalAddressesLastLsn?: number
  contactsLastLsn?: number
  isSyncing: boolean
  active: boolean
  totalEntries: number
  nfonEntries?: number
}

// Von der Registry verwaltete lokale Benutzer eines Kunden (Anmeldung per
// Microsoft oder Passkey). Enthält nie Zugangsdaten, nur deren Anzahl.
export interface LocalUser {
  id: string
  email: string
  displayName: string
  status: 'active' | 'disabled'
  groupIds: string[]
  passkeyCount: number
  microsoftLinked: boolean
  invitationPending: boolean
  invitationExpiresAt?: string
  lastLoginAt?: string
  source: string
}

// `system` ist true für die beiden vorbelegten Gruppen (Administratoren und
// Mitarbeiter); diese lassen sich nicht löschen.
export interface LocalGroup {
  id: string
  name: string
  description: string
  system: boolean
  memberIds: string[]
}

export interface LocalLoginSettings {
  loginMode: 'oidc' | 'local'
  autoJoin: 'create_user' | 'existing_only'
  adminGroupId: string
  defaultGroupId: string
  domains: string[]
  // No active member of the administrator group exists yet.
  adminMissing?: boolean
}
